/**
 * Timezone-aware time bucketing utility for dashboard charts
 *
 * Replaces hardcoded millisecond bucket calculations with proper
 * calendar-aligned and time-aligned bucketing using date-fns-tz.
 *
 * Fixes the persona aggregation bug where daily buckets were incorrectly
 * aligned to UTC instead of user's local timezone.
 */

import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfWeek,
} from "date-fns"
import { fromZonedTime, toZonedTime } from "date-fns-tz"

import { canonicalizeTimezone } from "@/utils/date/timezone"

import { extractTimestamp, type MetricValue } from "./chartShared"

/**
 * Semantic bucket types to replace magic millisecond numbers
 */
export type BucketType =
  | "minute" // 1 minute
  | "5minutes" // 5 minutes
  | "15minutes" // 15 minutes
  | "30minutes" // 30 minutes
  | "hour" // 1 hour
  | "2hours" // 2 hours
  | "4hours" // 4 hours
  | "day" // 1 day (calendar-aligned)
  | "week" // 1 week (calendar-aligned)
  | "month" // 1 month (calendar-aligned)

/**
 * Configuration for bucket creation
 */
interface BucketConfig {
  type: BucketType
  timezone?: string // IANA timezone (e.g., 'America/New_York'). Defaults to browser timezone
}

/**
 * Bucket result containing bucketed data points
 */
interface BucketResult<T = MetricValue> {
  bucketKey: number // Unix timestamp of bucket start
  bucketStart: Date // Bucket start date in local timezone
  bucketEnd: Date // Bucket end date in local timezone
  values: number[] // Extracted numeric values
  rawData: T[] // Original data points
}

/**
 * Get the browser's current timezone
 */
export const getBrowserTimezone = (): string => {
  return canonicalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

/**
 * Get bucket duration and alignment functions based on bucket type
 */
const getBucketInfo = (bucketType: BucketType) => {
  switch (bucketType) {
    case "minute":
      return {
        alignStart: startOfMinute,
        addDuration: (date: Date) => addMinutes(date, 1),
        isCalendarAligned: false,
      }
    case "5minutes":
      return {
        alignStart: (date: Date) => {
          const aligned = startOfMinute(date)
          const minutes = aligned.getMinutes()
          const roundedMinutes = Math.floor(minutes / 5) * 5
          return new Date(
            aligned.getFullYear(),
            aligned.getMonth(),
            aligned.getDate(),
            aligned.getHours(),
            roundedMinutes,
            0,
            0,
          )
        },
        addDuration: (date: Date) => addMinutes(date, 5),
        isCalendarAligned: false,
      }
    case "15minutes":
      return {
        alignStart: (date: Date) => {
          const aligned = startOfMinute(date)
          const minutes = aligned.getMinutes()
          const roundedMinutes = Math.floor(minutes / 15) * 15
          return new Date(
            aligned.getFullYear(),
            aligned.getMonth(),
            aligned.getDate(),
            aligned.getHours(),
            roundedMinutes,
            0,
            0,
          )
        },
        addDuration: (date: Date) => addMinutes(date, 15),
        isCalendarAligned: false,
      }
    case "30minutes":
      return {
        alignStart: (date: Date) => {
          const aligned = startOfMinute(date)
          const minutes = aligned.getMinutes()
          const roundedMinutes = Math.floor(minutes / 30) * 30
          return new Date(
            aligned.getFullYear(),
            aligned.getMonth(),
            aligned.getDate(),
            aligned.getHours(),
            roundedMinutes,
            0,
            0,
          )
        },
        addDuration: (date: Date) => addMinutes(date, 30),
        isCalendarAligned: false,
      }
    case "hour":
      return {
        alignStart: startOfHour,
        addDuration: (date: Date) => addHours(date, 1),
        isCalendarAligned: false,
      }
    case "2hours":
      return {
        alignStart: (date: Date) => {
          const aligned = startOfHour(date)
          const hours = aligned.getHours()
          const roundedHours = Math.floor(hours / 2) * 2
          return new Date(
            aligned.getFullYear(),
            aligned.getMonth(),
            aligned.getDate(),
            roundedHours,
            0,
            0,
            0,
          )
        },
        addDuration: (date: Date) => addHours(date, 2),
        isCalendarAligned: false,
      }
    case "4hours":
      return {
        alignStart: (date: Date) => {
          const aligned = startOfHour(date)
          const hours = aligned.getHours()
          const roundedHours = Math.floor(hours / 4) * 4
          return new Date(
            aligned.getFullYear(),
            aligned.getMonth(),
            aligned.getDate(),
            roundedHours,
            0,
            0,
            0,
          )
        },
        addDuration: (date: Date) => addHours(date, 4),
        isCalendarAligned: false,
      }
    case "day":
      return {
        alignStart: startOfDay,
        addDuration: (date: Date) => addDays(date, 1),
        isCalendarAligned: true,
      }
    case "week":
      return {
        alignStart: startOfWeek,
        addDuration: (date: Date) => addWeeks(date, 1),
        isCalendarAligned: true,
      }
    case "month":
      return {
        alignStart: startOfMonth,
        addDuration: (date: Date) => addMonths(date, 1),
        isCalendarAligned: true,
      }
    default:
      throw new Error(`Unsupported bucket type: ${String(bucketType)}`)
  }
}

/**
 * Create timezone-aware buckets for metric data
 *
 * This replaces the problematic UTC-aligned bucket logic in both
 * BarChartWidgetChart and LineChartWidgetChart with proper timezone handling.
 *
 * @param metricValues - Array of metric data points
 * @param config - Bucket configuration with type and optional timezone
 * @param dateRange - Time range to create buckets for
 * @returns Map of bucket timestamps to bucket data
 */
export const createTimezoneBuckets = <T extends MetricValue>(
  metricValues: T[],
  config: BucketConfig,
  dateRange: { start: Date; end: Date },
): Map<number, BucketResult<T>> => {
  const timezone = canonicalizeTimezone(config.timezone || getBrowserTimezone())
  const { alignStart, addDuration } = getBucketInfo(config.type)

  // Convert date range to local timezone for proper bucket alignment
  const localStart = toZonedTime(dateRange.start, timezone)
  const localEnd = toZonedTime(dateRange.end, timezone)

  const buckets = new Map<number, BucketResult<T>>()

  // Create buckets aligned to calendar/time boundaries in local timezone
  let currentBucketStart = alignStart(localStart)

  while (
    isBefore(currentBucketStart, localEnd) ||
    isEqual(currentBucketStart, localEnd)
  ) {
    const currentBucketEnd = addDuration(currentBucketStart)

    // Convert bucket boundaries back to UTC for storage as timestamps
    const bucketStartUtc = fromZonedTime(currentBucketStart, timezone)

    const bucketKey = bucketStartUtc.getTime()

    buckets.set(bucketKey, {
      bucketKey,
      bucketStart: currentBucketStart,
      bucketEnd: currentBucketEnd,
      values: [],
      rawData: [],
    })

    currentBucketStart = currentBucketEnd
  }

  // Assign data points to appropriate buckets
  metricValues.forEach((point) => {
    const pointTimestamp = extractTimestamp(point)
    const pointDate = new Date(pointTimestamp)
    const localPointDate = toZonedTime(pointDate, timezone)

    // Find the correct bucket for this data point
    for (const [, bucket] of buckets.entries()) {
      const isInBucket =
        (isAfter(localPointDate, bucket.bucketStart) ||
          isEqual(localPointDate, bucket.bucketStart)) &&
        isBefore(localPointDate, bucket.bucketEnd)

      if (isInBucket) {
        bucket.values.push(point.value)
        bucket.rawData.push(point)
        break
      }
    }
  })

  return buckets
}

/**
 * Convert bucket type back to milliseconds for backward compatibility
 *
 * This is used during transition period to maintain compatibility
 * with existing code that expects millisecond bucket sizes.
 *
 * @param bucketType - Semantic bucket type
 * @returns Equivalent duration in milliseconds
 */
export const bucketTypeToMilliseconds = (bucketType: BucketType): number => {
  switch (bucketType) {
    case "minute":
      return 60 * 1000
    case "5minutes":
      return 5 * 60 * 1000
    case "15minutes":
      return 15 * 60 * 1000
    case "30minutes":
      return 30 * 60 * 1000
    case "hour":
      return 60 * 60 * 1000
    case "2hours":
      return 2 * 60 * 60 * 1000
    case "4hours":
      return 4 * 60 * 60 * 1000
    case "day":
      return 24 * 60 * 60 * 1000
    case "week":
      return 7 * 24 * 60 * 60 * 1000
    case "month":
      return 30 * 24 * 60 * 60 * 1000 // Approximate
    default:
      throw new Error(`Unsupported bucket type: ${String(bucketType)}`)
  }
}

/**
 * Convert milliseconds back to bucket type for backward compatibility
 *
 * @param milliseconds - Duration in milliseconds
 * @returns Corresponding semantic bucket type
 */
export const millisecondsToBucketType = (milliseconds: number): BucketType => {
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day

  switch (milliseconds) {
    case minute:
      return "minute"
    case 5 * minute:
      return "5minutes"
    case 15 * minute:
      return "15minutes"
    case 30 * minute:
      return "30minutes"
    case hour:
      return "hour"
    case 2 * hour:
      return "2hours"
    case 4 * hour:
      return "4hours"
    case day:
      return "day"
    case week:
      return "week"
    default:
      throw new Error(`Unsupported millisecond duration: ${milliseconds}`)
  }
}
